// MF-67 — Journal configuration: every setup screen in one tabbed page, in the order they build on
// each other — Types (the tone) → Categories (the kind of record, under a type) → Severities (the
// degrees inside a category) → Zones → Templates (the reason: a category, one of its severities or
// "not applicable", and the points).
// Each tab only shows for roles that hold its permission; add/edit forms render inside the same page.
import { Navigate, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Module from '../../../layout/Module'
import TabsLink from '../../../tabs/TabsLink'
import NotFoundPage from '../../../NotFoundPage/NotFoundPage'
import { CONFIG_PATH, configPath } from '../paths'
import TypesList from '../Types/TypesList'
import TypeForm from '../Types/TypeForm'
import SeveritiesList from '../Severities/SeveritiesList'
import SeverityForm from '../Severities/SeverityForm'
import ZonesList from '../Zones/ZonesList'
import ZoneForm from '../Zones/ZoneForm'
import CategoriesList from '../Categories/CategoriesList'
import CategoryForm from '../Categories/CategoryForm'
import TemplatesList from '../Templates/TemplatesList'
import TemplateForm from '../Templates/TemplateForm'

// In build order. `key` = permission key (lower-cased module name) and URL segment.
export const CONFIG_TABS = [
  {
    key: 'journaltypes',
    tab: 'types',
    name: '1 · Types',
    List: TypesList,
    Form: TypeForm
  },
  {
    key: 'journalcategories',
    tab: 'categories',
    name: '2 · Categories',
    List: CategoriesList,
    Form: CategoryForm
  },
  {
    key: 'journalseverities',
    tab: 'severities',
    name: '3 · Severities',
    List: SeveritiesList,
    Form: SeverityForm
  },
  { key: 'journalzones', tab: 'zones', name: '4 · Zones', List: ZonesList, Form: ZoneForm },
  {
    key: 'journaltemplates',
    tab: 'templates',
    name: '5 · Templates',
    List: TemplatesList,
    Form: TemplateForm
  }
]

const JournalConfiguration = () => {
  const permissions = useSelector((state) => state?.session?.permissions || {})
  const allowed = CONFIG_TABS.filter((t) => permissions[t.key])

  return (
    <Module>
      <Module.Header title='Journal Configuration' titleLink={CONFIG_PATH} />
      <Module.Body>
        <Module.BodyHeader>
          <div className='relative overflow-hidden w-full h-full flex items-end'>
            <TabsLink
              id='journal-configuration'
              items={CONFIG_TABS.map((t) => ({
                id: t.tab,
                name: t.name,
                path: configPath(t.tab),
                permission: Boolean(permissions[t.key])
              }))}
            />
          </div>
        </Module.BodyHeader>
        <Module.BodyContainer classNameWrapper='overflow-auto'>
          <Routes>
            <Route
              index
              element={
                allowed.length ? <Navigate to={configPath(allowed[0].tab)} replace /> : <NotFoundPage />
              }
            />
            {allowed.map(({ tab, List, Form }) => [
              <Route key={tab} path={tab} element={<List />} />,
              <Route key={`${tab}-add`} path={`${tab}/add`} element={<Form />} />,
              <Route key={`${tab}-edit`} path={`${tab}/edit/:id`} element={<Form />} />
            ])}
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
        </Module.BodyContainer>
      </Module.Body>
    </Module>
  )
}

export default JournalConfiguration
